import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import { Calendar } from 'react-native-calendars';
import moment from 'moment-timezone';
import Toast from 'react-native-root-toast';
import { useNavigation } from '@react-navigation/native';

import { useUserContext } from '../../../common/utils/context';
import { getUserName } from '../../../common/utils/user/getUserName';
import { getLocalTime } from '../../../common/utils/time/time';
import { CREATE_SERVICE_RUN } from '../../../common/graphql/serviceRun';
import { POS_TYPE, SERVICE_RUN_SOURCE } from '../../../common/constants';
import { Text } from '../../components';
import { ActionDock, Card, ListRow, PrimaryButton, Screen, SectionTitle, Sheet } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import {
	GET_PLANNING_LOCATIONS,
	GET_PLANNING_MACHINES,
	GET_PLANNING_ROUTES,
	GET_PLANNING_USERS,
	GET_PLANNING_WAREHOUSES,
} from '../../graphql/serviceRunPlanning';
import ServiceRunPlanningPicker from './ServiceRunPlanningPicker';

const USER_FILTER = { enabled: true, excludeFromSelectors: false };
const EMPTY_ITEMS = [];
const pointOfSaleName = item => [item.assetId, item.location?.name, item.place?.name].filter(Boolean).join(' · ') || 'Unnamed point of sale';

const ServiceRunCreatorView = () => {
	const navigation = useNavigation();
	const { user, isDriver, tenant } = useUserContext();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors, type } } } = useTheme();
	const timezone = tenant?.timezone || 'Australia/Melbourne';
	const [date, setDate] = useState(() => getLocalTime(timezone).format('YYYY-MM-DD'));
	const [driver, setDriver] = useState(user);
	const [routeIds, setRouteIds] = useState(null);
	// null means every matching point of sale; [] is an explicit empty selection.
	const [machineIds, setMachineIds] = useState(null);
	const [marketIds, setMarketIds] = useState(null);
	const [overrides, setOverrides] = useState({});
	const [picker, setPicker] = useState(null);
	const [showDate, setShowDate] = useState(false);
	const [createRun, { loading: submitting }] = useMutation(CREATE_SERVICE_RUN);
	const routesQuery = useQuery(GET_PLANNING_ROUTES, { fetchPolicy: 'network-only' });
	const routes = routesQuery.data?.getRoutes?.items || EMPTY_ITEMS;

	useEffect(() => {
		if (routesQuery.data && routeIds === null) {
			setRouteIds(isDriver ? routes.filter(route => route.driver?.id === user.id).map(route => route.id) : []);
		}
	}, [isDriver, routeIds, routes, routesQuery.data, user.id]);

	const filter = useMemo(() => ({ routes: routeIds || [] }), [routeIds]);
	const machinesQuery = useQuery(GET_PLANNING_MACHINES, {
		variables: { filter },
		skip: routeIds === null,
		fetchPolicy: 'network-only',
	});
	// Never submit the previous route's rows while a new filter is loading.
	const points = machinesQuery.loading ? EMPTY_ITEMS : machinesQuery.data?.getMachines?.items || EMPTY_ITEMS;
	const machines = points.filter(item => item.type !== POS_TYPE.MARKET);
	const markets = points.filter(item => item.type === POS_TYPE.MARKET);
	const selectedMachines = machines.filter(item => machineIds === null || machineIds.includes(item.id));
	const selectedMarkets = markets.filter(item => marketIds === null || marketIds.includes(item.id));
	const selectedPoints = [...selectedMachines, ...selectedMarkets];
	const defaultsRoute = routeIds?.length ? routes.find(route => route.id === routeIds[0]) : routes[0];
	const defaults = {
		startLocation: defaultsRoute?.defaultStartLocation,
		endLocation: defaultsRoute?.defaultEndLocation,
		warehouseId: defaultsRoute?.warehouse,
		returnWarehouseId: defaultsRoute?.returnWarehouse,
		...(overrides.routeId === defaultsRoute?.id ? overrides : {}),
	};
	defaults.returnWarehouseId = defaults.returnWarehouseId || defaults.warehouseId;
	const countSummary = `${selectedMachines.length} machine${selectedMachines.length === 1 ? '' : 's'} · ${selectedMarkets.length} market${selectedMarkets.length === 1 ? '' : 's'} selected`;
	const selectedDate = moment.tz(date, 'YYYY-MM-DD', timezone);
	const error = routesQuery.error || machinesQuery.error;
	const loading = !error && (routesQuery.loading || routeIds === null || machinesQuery.loading);
	const validation = !defaultsRoute ? 'No route is available. Add a route before planning a service run.'
		: !selectedPoints.length ? 'Select at least one machine or market.'
			: !driver?.id ? 'Choose a user for this service run.' : null;

	const changeRoutes = ids => {
		setRouteIds(ids || []);
		setOverrides({});
	};
	const clearFilters = () => {
		changeRoutes([]);
		setMachineIds(null);
		setMarketIds(null);
	};
	const submit = async () => {
		if (loading || error || validation || submitting) {
			return;
		}
		try {
			const result = await createRun({ variables: { input: {
				routeId: defaultsRoute.id,
				driver: driver.id,
				planned: selectedDate.clone().hour(12).minute(0).second(0).millisecond(0).toISOString(),
				machines: selectedPoints.map(item => item.id),
				startLocation: defaults.startLocation?.id,
				endLocation: defaults.endLocation?.id,
				warehouseId: defaults.warehouseId?.id,
				returnWarehouseId: defaults.returnWarehouseId?.id,
				source: SERVICE_RUN_SOURCE.CREATOR,
			} } });
			navigation.replace('ServiceRunDetailView', { id: result.data.createServiceRun.id });
			Toast.show('Service run successfully created');
		}
		catch (createError) {
			Toast.show(createError.message, { duration: Toast.durations.LONG });
		}
	};
	const openDefaultsPicker = (key, title, locations = false) => setPicker({
		title,
		document: locations ? GET_PLANNING_LOCATIONS : GET_PLANNING_WAREHOUSES,
		field: locations ? 'getLocations' : 'getWarehouses',
		selected: defaults[key]?.id ? [defaults[key].id] : [],
		onChange: item => setOverrides(current => ({ ...(current.routeId === defaultsRoute.id ? current : {}), routeId: defaultsRoute.id, [key]: item })),
	});
	const openPointPicker = (title, items, selected, onChange) => setPicker({
		title, items: items.map(item => ({ ...item, name: pointOfSaleName(item) })),
		selected, onChange, multiple: true, allLabel: `All ${title.toLowerCase()}`,
	});

	return (
		<Screen
			heading="Plan service run"
			subheading={selectedDate.format('dddd D MMMM YYYY')}
			isLoading={loading}
			error={error?.message}
			onRetry={() => { routesQuery.refetch(); machinesQuery.refetch(); }}
			dock={<ActionDock hint={countSummary} action={{ label: 'Create service run', onPress: submit, loading: submitting, disabled: Boolean(validation) }} />}
		>
			<Card style={styles.section}>
				<View style={styles.sectionHeader}>
					<SectionTitle title="Filters" />
					<Pressable onPress={clearFilters} accessibilityRole="button" style={styles.clear}><Text style={styles.link}>Clear all</Text></Pressable>
				</View>
				<ListRow title="Routes" subtitle={routeIds?.length ? routeIds.map(id => routes.find(route => route.id === id)?.name).filter(Boolean).join(', ') : 'All routes'} onPress={() => setPicker({ title: 'Routes', items: routes, selected: routeIds?.length ? routeIds : null, multiple: true, allLabel: 'All routes', onChange: changeRoutes })} />
				<ListRow title="Machines" subtitle={machineIds === null ? 'All machines' : `${selectedMachines.length} selected`} onPress={() => openPointPicker('Machines', machines, machineIds, setMachineIds)} />
				<ListRow title="Markets" subtitle={marketIds === null ? 'All markets' : `${selectedMarkets.length} selected`} onPress={() => openPointPicker('Markets', markets, marketIds, setMarketIds)} />
				<Text style={styles.note}>{countSummary}</Text>
			</Card>
			<Card style={styles.section}>
				<SectionTitle title="Assignment" />
				<ListRow title="Assign user" subtitle={getUserName(driver)} icon="user" onPress={() => setPicker({ title: 'Assign user', document: GET_PLANNING_USERS, field: 'getUsers', filter: USER_FILTER, getName: getUserName, selected: [driver.id], onChange: setDriver })} />
				<ListRow title="Choose date" subtitle={selectedDate.format('dddd D MMMM YYYY')} icon="calendar" onPress={() => setShowDate(true)} />
			</Card>
			{Boolean(defaultsRoute) && (
				<Card style={styles.section}>
					<SectionTitle title="Run defaults" />
					<Text style={styles.note}>Defaults taken from {defaultsRoute.name}</Text>
					<ListRow title="Pick warehouse" subtitle={defaults.warehouseId?.name || 'Choose warehouse'} onPress={() => openDefaultsPicker('warehouseId', 'Pick warehouse')} />
					<ListRow title="Return warehouse" subtitle={defaults.returnWarehouseId?.name || 'Choose warehouse'} onPress={() => openDefaultsPicker('returnWarehouseId', 'Return warehouse')} />
					<ListRow title="Start location" subtitle={defaults.startLocation?.name || 'Choose location'} onPress={() => openDefaultsPicker('startLocation', 'Start location', true)} />
					<ListRow title="End location" subtitle={defaults.endLocation?.name || 'Choose location'} onPress={() => openDefaultsPicker('endLocation', 'End location', true)} />
				</Card>
			)}
			{Boolean(validation) && <Text style={styles.error}>{validation}</Text>}
			{Boolean(picker) && <ServiceRunPlanningPicker {...picker} onClose={() => setPicker(null)} />}
			{showDate && (
				<Sheet isVisible={true} onClose={() => setShowDate(false)} title="Choose date" footer={<PrimaryButton label="Done" onPress={() => setShowDate(false)} />}>
					<Calendar
						current={date}
						onDayPress={day => setDate(day.dateString)}
						markedDates={{ [date]: { selected: true, selectedColor: colors.primary, selectedTextColor: colors.onPrimary } }}
						theme={{ calendarBackground: colors.surface, dayTextColor: colors.text, monthTextColor: colors.text, textSectionTitleColor: colors.textSecondary, textDisabledColor: colors.disabledText, todayTextColor: colors.accent, arrowColor: colors.accent, textDayFontFamily: type.body.fontFamily, textMonthFontFamily: type.bodyStrong.fontFamily }}
					/>
				</Sheet>
			)}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type, touch } }) => ({
	section: { gap: space.md },
	sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
	clear: { minHeight: touch.min, justifyContent: 'center', paddingHorizontal: space.sm },
	link: { ...type.bodySmallStrong, color: colors.accent },
	note: { ...type.secondary, color: colors.textSecondary },
	error: { ...type.bodySmall, color: colors.danger },
});

export default ServiceRunCreatorView;
